import React, { useState, useEffect, useMemo } from 'react';
import { Box, TextField, Tabs, Tab } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { DataGridPro, useGridApiRef } from '@mui/x-data-grid-pro';
import countryList from 'react-select-country-list';
import { getCountries, getPorts } from '../../../../features/master-data';
import {
  getUnloadingPoints,
  addCountry,
  updateCountry,
  deleteCountry,
  addPort,
  updatePort,
  deletePort,
  addUnloadingPoint,
  deleteUnloadingPoint,
} from '../../services/SettingsRepository';
import ExpSnackbarAlerts from '../../../../shared/components/display/ExpSnackbarAlerts';
import MultiSelectColumnFilter from '../../../../shared/components/datagrid/MultiSelectColumnFilter';
import useSnackbar from '../../../../shared/hooks/useSnackbar';
import useRowEditing from '../../../../shared/hooks/useRowEditing';
import getActionsColumn from '../../../../shared/components/datagrid/getActionsColumn';
import AddRowToolbar from '../../../../shared/components/datagrid/AddRowToolbar';
import { scrollToNewRow } from '../../../../shared/utils/scrollToNewRow';

// ─── Country Table ──────────────────────────────────────────────────────────────

const CountryTable = ({ countries, fetchAll }) => {
  const [rows, setRows] = useState([]);
  const { showSnackbar, snackbarProps } = useSnackbar();
  const apiRef = useGridApiRef();
  const allCountryCodes = useMemo(() => countryList().getValues(), []);

  const [filterModel, setFilterModel] = useState(() => {
    const saved = localStorage.getItem('countryTableFilter');
    return saved ? JSON.parse(saved) : { items: [] };
  });

  useEffect(() => {
    localStorage.setItem('countryTableFilter', JSON.stringify(filterModel));
  }, [filterModel]);

  useEffect(() => {
    setRows(countries.map((c) => ({ ...c, id: c.countryCode })));
  }, [countries]);

  const {
    rowModesModel,
    handleRowEditStop,
    handleEditClick,
    handleSaveClick,
    handleCancelClick,
    handleRowModesModelChange,
    startEditingRow,
  } = useRowEditing({ rows, setRows });

  const handleDeleteClick = (id) => async () => {
    try {
      await deleteCountry(id);
      await fetchAll();
      showSnackbar('Country deleted successfully!');
    } catch (error) {
      console.error('Error deleting country:', error);
      showSnackbar('Error deleting country.', 'error');
    }
  };

  const processRowUpdate = async (newRow) => {
    if (!newRow.countryCode || !newRow.name) {
      showSnackbar('Country code and name are required.', 'error');
      throw new Error('Country code and name are required.');
    }
    if (
      newRow.salesDay != null &&
      newRow.salesDay !== '' &&
      (Number(newRow.salesDay) < 1 ||
        Number(newRow.salesDay) > 31 ||
        !Number.isInteger(Number(newRow.salesDay)))
    ) {
      showSnackbar(
        'Sales Effectiveness Day must be a whole number between 1 and 31.',
        'error'
      );
      throw new Error(
        'Sales Effectiveness Day must be a whole number between 1 and 31.'
      );
    }
    try {
      if (newRow.isNew) {
        await addCountry({
          countryCode: newRow.countryCode,
          name: newRow.name,
          salesDay: newRow.salesDay ? Number(newRow.salesDay) : null,
        });
        showSnackbar('Country added successfully!');
      } else {
        await updateCountry(newRow.countryCode, {
          name: newRow.name,
          salesDay: newRow.salesDay ? Number(newRow.salesDay) : null,
        });
        showSnackbar('Country updated successfully!');
      }
      await fetchAll();
    } catch (error) {
      console.error('Error saving country:', error);
      showSnackbar('Error saving country.', 'error');
      if (newRow.isNew) {
        setRows((prev) => prev.filter((r) => r.id !== newRow.id));
      }
      throw error;
    }
    return { ...newRow, isNew: false };
  };

  const handleAddClick = () => {
    const tempId = `new-${Date.now()}`;
    setRows((old) => [
      ...old,
      { id: tempId, countryCode: '', name: '', salesDay: null, isNew: true },
    ]);
    startEditingRow(tempId, 'countryCode');
    setFilterModel({ items: [] });
    scrollToNewRow(apiRef, rows.length + 1);
  };

  const columns = [
    {
      field: 'countryCode',
      headerName: 'Country Code',
      width: 160,
      editable: true,
      renderEditCell: (params) => {
        const usedCodes = rows
          .filter((r) => !r.isNew)
          .map((r) => r.countryCode);
        const available = allCountryCodes.filter((c) => !usedCodes.includes(c));
        return (
          <Autocomplete
            options={available}
            value={params.value || null}
            onChange={(_, val) => {
              params.api.setEditCellValue({
                id: params.id,
                field: 'countryCode',
                value: val || '',
              });
              if (val) {
                const label = countryList().getLabel(val);
                params.api.setEditCellValue({
                  id: params.id,
                  field: 'name',
                  value: label,
                });
              }
            }}
            renderInput={(p) => (
              <TextField {...p} label='Code' sx={{ width: 140 }} />
            )}
            disableClearable
            size='small'
          />
        );
      },
    },
    {
      field: 'name',
      headerName: 'Country Name',
      width: 250,
      editable: true,
      flex: 1,
    },
    {
      field: 'salesDay',
      headerName: 'Sales Effectiveness Day',
      width: 200,
      editable: true,
      type: 'number',
      renderCell: (params) => params.value ?? '—',
      renderEditCell: (params) => (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            height: '100%',
            px: 1,
          }}
        >
          <TextField
            value={params.value != null ? params.value : ''}
            onChange={(e) => {
              const val = e.target.value;
              if (
                val === '' ||
                (/^\d{1,2}$/.test(val) && Number(val) >= 1 && Number(val) <= 31)
              ) {
                params.api.setEditCellValue({
                  id: params.id,
                  field: 'salesDay',
                  value: val === '' ? null : Number(val),
                });
              }
            }}
            inputProps={{
              maxLength: 2,
              inputMode: 'numeric',
              pattern: '[0-9]*',
              style: { padding: 0 },
            }}
            placeholder='1-31'
            size='small'
            variant='standard'
            sx={{ flex: 1 }}
          />
        </Box>
      ),
    },
    getActionsColumn({
      rowModesModel,
      handleEditClick,
      handleSaveClick,
      handleCancelClick,
      handleDeleteClick,
    }),
  ];

  return (
    <Box height='50vh'>
      <DataGridPro
        apiRef={apiRef}
        pagination
        rows={rows}
        columns={columns}
        editMode='row'
        isCellEditable={(params) =>
          params.field !== 'countryCode' || !!params.row.isNew
        }
        rowModesModel={rowModesModel}
        onRowModesModelChange={handleRowModesModelChange}
        onRowEditStop={handleRowEditStop}
        processRowUpdate={processRowUpdate}
        onProcessRowUpdateError={(error) =>
          console.error('Row update error:', error)
        }
        filterMode='client'
        filterModel={filterModel}
        onFilterModelChange={setFilterModel}
        slots={{ toolbar: AddRowToolbar }}
        slotProps={{ toolbar: { label: 'Add Country', onAdd: handleAddClick } }}
        pageSizeOptions={[10, 25, 50, 100]}
        initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
      />
      <ExpSnackbarAlerts {...snackbarProps} />
    </Box>
  );
};

// ─── Port Table ─────────────────────────────────────────────────────────────────

const PortTable = ({ ports, countries, fetchAll }) => {
  const [rows, setRows] = useState([]);
  const { showSnackbar, snackbarProps } = useSnackbar();
  const apiRef = useGridApiRef();

  const [filterModel, setFilterModel] = useState(() => {
    const saved = localStorage.getItem('portTableFilter');
    return saved ? JSON.parse(saved) : { items: [] };
  });

  useEffect(() => {
    localStorage.setItem('portTableFilter', JSON.stringify(filterModel));
  }, [filterModel]);

  useEffect(() => {
    setRows(ports.map((p) => ({ ...p, id: p.unLocode })));
  }, [ports]);

  const {
    rowModesModel,
    handleRowEditStop,
    handleEditClick,
    handleSaveClick,
    handleCancelClick,
    handleRowModesModelChange,
    startEditingRow,
  } = useRowEditing({ rows, setRows });

  const handleDeleteClick = (id) => async () => {
    try {
      await deletePort(id);
      await fetchAll();
      showSnackbar('Port deleted successfully!');
    } catch (error) {
      console.error('Error deleting port:', error);
      showSnackbar('Error deleting port.', 'error');
    }
  };

  const processRowUpdate = async (newRow) => {
    if (!newRow.country || !newRow.name) {
      showSnackbar('Country and port name are required.', 'error');
      throw new Error('Country and port name are required.');
    }
    const countryCode = newRow.country.countryCode || newRow.country;
    try {
      if (newRow.isNew) {
        if (!newRow.unLocode) {
          showSnackbar('UN-Locode is required.', 'error');
          throw new Error('UN-Locode is required.');
        }
        await addPort(countryCode, {
          unLocode: newRow.unLocode,
          name: newRow.name,
        });
        showSnackbar('Port added successfully!');
      } else {
        await updatePort(countryCode, newRow.unLocode, { name: newRow.name });
        showSnackbar('Port updated successfully!');
      }
      await fetchAll();
    } catch (error) {
      console.error('Error saving port:', error);
      showSnackbar('Error saving port.', 'error');
      if (newRow.isNew) {
        setRows((prev) => prev.filter((r) => r.id !== newRow.id));
      }
      throw error;
    }
    return { ...newRow, isNew: false };
  };

  const handleAddClick = () => {
    const tempId = `new-${Date.now()}`;
    setRows((old) => [
      ...old,
      { id: tempId, unLocode: '', name: '', country: null, isNew: true },
    ]);
    startEditingRow(tempId, 'unLocode');
    setFilterModel({ items: [] });
    scrollToNewRow(apiRef, rows.length + 1);
  };

  const columns = [
    {
      field: 'unLocode',
      headerName: 'UN-Locode',
      width: 140,
      editable: true,
      renderEditCell: (params) => (
        <TextField
          value={params.value || ''}
          onChange={(e) => {
            params.api.setEditCellValue({
              id: params.id,
              field: 'unLocode',
              value: e.target.value.toUpperCase().slice(0, 5),
            });
          }}
          inputProps={{ maxLength: 5 }}
          size='small'
          variant='standard'
          sx={{ px: 1 }}
        />
      ),
    },
    {
      field: 'name',
      headerName: 'Port Name',
      width: 250,
      editable: true,
      flex: 1,
    },
    {
      field: 'country',
      headerName: 'Country',
      width: 250,
      editable: true,
      filterable: true,
      renderCell: (params) =>
        params.row.country
          ? `${params.row.country.countryCode} - ${params.row.country.name}`
          : '—',
      filterOperators: [
        {
          label: 'is any of',
          value: 'isAnyOf',
          getApplyFilterFn: (filterItem) => {
            if (!filterItem.value || filterItem.value.length === 0) return null;
            return (params) => {
              const cc = params?.countryCode;
              return cc ? filterItem.value.includes(cc) : false;
            };
          },
          InputComponent: (props) => {
            const options = [
              ...new Map(
                rows
                  .filter((r) => r.country?.countryCode)
                  .map((r) => [
                    r.country.countryCode,
                    {
                      value: r.country.countryCode,
                      label: `${r.country.countryCode} - ${r.country.name}`,
                    },
                  ])
              ).values(),
            ].sort((a, b) => a.label.localeCompare(b.label));
            return (
              <MultiSelectColumnFilter
                item={props.item}
                applyValue={props.applyValue}
                options={options}
              />
            );
          },
        },
      ],
      sortComparator: (v1, v2) =>
        (v1?.name || '').localeCompare(v2?.name || ''),
      renderEditCell: (params) => (
        <Autocomplete
          options={countries}
          getOptionLabel={(opt) =>
            opt?.countryCode ? `${opt.countryCode} - ${opt.name}` : ''
          }
          value={params.row.country || null}
          isOptionEqualToValue={(opt, val) =>
            val ? opt.countryCode === val.countryCode : false
          }
          onChange={(_, val) => {
            params.api.setEditCellValue({
              id: params.id,
              field: 'country',
              value: val || null,
            });
          }}
          renderInput={(p) => (
            <TextField {...p} label='Country' sx={{ width: 230 }} />
          )}
        />
      ),
    },
    getActionsColumn({
      rowModesModel,
      handleEditClick,
      handleSaveClick,
      handleCancelClick,
      handleDeleteClick,
    }),
  ];

  return (
    <Box height='50vh'>
      <DataGridPro
        apiRef={apiRef}
        pagination
        rows={rows}
        columns={columns}
        editMode='row'
        isCellEditable={(params) =>
          params.field !== 'unLocode' || !!params.row.isNew
        }
        rowModesModel={rowModesModel}
        onRowModesModelChange={handleRowModesModelChange}
        onRowEditStop={handleRowEditStop}
        processRowUpdate={processRowUpdate}
        onProcessRowUpdateError={(error) =>
          console.error('Row update error:', error)
        }
        filterMode='client'
        filterModel={filterModel}
        onFilterModelChange={setFilterModel}
        slots={{ toolbar: AddRowToolbar }}
        slotProps={{ toolbar: { label: 'Add Port', onAdd: handleAddClick } }}
        pageSizeOptions={[10, 25, 50, 100]}
        initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
      />
      <ExpSnackbarAlerts {...snackbarProps} />
    </Box>
  );
};

// ─── Unloading Point Table ──────────────────────────────────────────────────────

const UnloadingPointTable = ({ unloadingPoints, ports, fetchAll }) => {
  const [rows, setRows] = useState([]);
  const { showSnackbar, snackbarProps } = useSnackbar();
  const apiRef = useGridApiRef();

  const [filterModel, setFilterModel] = useState(() => {
    const saved = localStorage.getItem('unloadingPointTableFilter');
    return saved ? JSON.parse(saved) : { items: [] };
  });

  useEffect(() => {
    localStorage.setItem(
      'unloadingPointTableFilter',
      JSON.stringify(filterModel)
    );
  }, [filterModel]);

  useEffect(() => {
    setRows(unloadingPoints.map((u) => ({ ...u, id: u.unloadingCode })));
  }, [unloadingPoints]);

  const {
    rowModesModel,
    handleRowEditStop,
    handleEditClick,
    handleSaveClick,
    handleCancelClick,
    handleRowModesModelChange,
    startEditingRow,
  } = useRowEditing({ rows, setRows });

  const handleDeleteClick = (id) => async () => {
    try {
      await deleteUnloadingPoint(id);
      await fetchAll();
      showSnackbar('Unloading point deleted successfully!');
    } catch (error) {
      console.error('Error deleting unloading point:', error);
      showSnackbar('Error deleting unloading point.', 'error');
    }
  };

  const processRowUpdate = async (newRow) => {
    if (!newRow.unloadingCode || !newRow.port) {
      showSnackbar('Unloading code and port are required.', 'error');
      throw new Error('Unloading code and port are required.');
    }
    const unLocode = newRow.port.unLocode || newRow.port;
    try {
      if (newRow.isNew) {
        await addUnloadingPoint(unLocode, {
          unloadingCode: newRow.unloadingCode,
        });
        showSnackbar('Unloading point added successfully!');
      }
      await fetchAll();
    } catch (error) {
      console.error('Error saving unloading point:', error);
      showSnackbar('Error saving unloading point.', 'error');
      if (newRow.isNew) {
        setRows((prev) => prev.filter((r) => r.id !== newRow.id));
      }
      throw error;
    }
    return { ...newRow, isNew: false };
  };

  const handleAddClick = () => {
    const tempId = `new-${Date.now()}`;
    setRows((old) => [
      ...old,
      { id: tempId, unloadingCode: '', port: null, isNew: true },
    ]);
    startEditingRow(tempId, 'unloadingCode');
    setFilterModel({ items: [] });
    scrollToNewRow(apiRef, rows.length + 1);
  };

  const columns = [
    {
      field: 'unloadingCode',
      headerName: 'Unloading Code',
      width: 200,
      editable: true,
    },
    {
      field: 'port',
      headerName: 'Port',
      width: 300,
      editable: true,
      flex: 1,
      filterable: true,
      renderCell: (params) =>
        params.row.port
          ? `${params.row.port.unLocode} - ${params.row.port.name}`
          : '—',
      filterOperators: [
        {
          label: 'is any of',
          value: 'isAnyOf',
          getApplyFilterFn: (filterItem) => {
            if (!filterItem.value || filterItem.value.length === 0) return null;
            return (params) => {
              const ul = params?.unLocode;
              return ul ? filterItem.value.includes(ul) : false;
            };
          },
          InputComponent: (props) => {
            const options = [
              ...new Map(
                rows
                  .filter((r) => r.port?.unLocode)
                  .map((r) => [
                    r.port.unLocode,
                    {
                      value: r.port.unLocode,
                      label: `${r.port.unLocode} - ${r.port.name}`,
                    },
                  ])
              ).values(),
            ].sort((a, b) => a.label.localeCompare(b.label));
            return (
              <MultiSelectColumnFilter
                item={props.item}
                applyValue={props.applyValue}
                options={options}
              />
            );
          },
        },
      ],
      sortComparator: (v1, v2) =>
        (v1?.unLocode || '').localeCompare(v2?.unLocode || ''),
      renderEditCell: (params) => (
        <Autocomplete
          options={ports}
          getOptionLabel={(opt) =>
            opt?.unLocode ? `${opt.unLocode} - ${opt.name}` : ''
          }
          value={params.row.port || null}
          isOptionEqualToValue={(opt, val) =>
            val ? opt.unLocode === val.unLocode : false
          }
          onChange={(_, val) => {
            params.api.setEditCellValue({
              id: params.id,
              field: 'port',
              value: val || null,
            });
          }}
          renderInput={(p) => (
            <TextField {...p} label='Port' sx={{ width: 280 }} />
          )}
        />
      ),
    },
    getActionsColumn({
      rowModesModel,
      handleEditClick,
      handleSaveClick,
      handleCancelClick,
      handleDeleteClick,
    }),
  ];

  return (
    <Box height='50vh'>
      <DataGridPro
        apiRef={apiRef}
        pagination
        rows={rows}
        columns={columns}
        editMode='row'
        isCellEditable={(params) =>
          params.field !== 'unloadingCode' || !!params.row.isNew
        }
        rowModesModel={rowModesModel}
        onRowModesModelChange={handleRowModesModelChange}
        onRowEditStop={handleRowEditStop}
        processRowUpdate={processRowUpdate}
        onProcessRowUpdateError={(error) =>
          console.error('Row update error:', error)
        }
        filterMode='client'
        filterModel={filterModel}
        onFilterModelChange={setFilterModel}
        slots={{ toolbar: AddRowToolbar }}
        slotProps={{
          toolbar: { label: 'Add Unloading Point', onAdd: handleAddClick },
        }}
        pageSizeOptions={[10, 25, 50, 100]}
        initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
      />
      <ExpSnackbarAlerts {...snackbarProps} />
    </Box>
  );
};

// ─── Combined Component ─────────────────────────────────────────────────────────

const CountryPortTable = () => {
  const [countries, setCountries] = useState([]);
  const [ports, setPorts] = useState([]);
  const [unloadingPoints, setUnloadingPoints] = useState([]);
  const [tab, setTab] = useState(0);

  const fetchAll = async () => {
    const [countriesRes, portsRes] = await Promise.all([
      getCountries(),
      getPorts(),
    ]);
    setCountries(countriesRes);
    setPorts(portsRes);
    try {
      const ulRes = await getUnloadingPoints();
      setUnloadingPoints(ulRes);
    } catch {
      setUnloadingPoints([]);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  return (
    <Box>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label={`Countries (${countries.length})`} />
        <Tab label={`Ports (${ports.length})`} />
        <Tab label={`Unloading Points (${unloadingPoints.length})`} />
      </Tabs>
      {tab === 0 && <CountryTable countries={countries} fetchAll={fetchAll} />}
      {tab === 1 && (
        <PortTable ports={ports} countries={countries} fetchAll={fetchAll} />
      )}
      {tab === 2 && (
        <UnloadingPointTable
          unloadingPoints={unloadingPoints}
          ports={ports}
          fetchAll={fetchAll}
        />
      )}
    </Box>
  );
};

export default CountryPortTable;
